import type { TraceCommitment } from "../api/types";
import { certLabel } from "./TraceStatusPanel";

/**
 * What was bought, and what it has to arrive with.
 *
 * A purchase order used to render one figure — the total. That is the derived
 * half: unit price is the term that was agreed and the total follows from it,
 * so a screen showing only the total cannot answer what a partial shipment is
 * worth or whether an invoice line is right. These components put the line back
 * on the order, beside the identity of the units and the paperwork the supplier
 * undertook to ship.
 */

/**
 * One serial per unit, or one lot for the whole line.
 *
 * Absent is the ordinary case and is stated as such rather than left blank — a
 * blank reads as "nobody filled this in", where the truth is usually that a
 * distributor assigns a serial at pick and the identity is established at
 * goods-in. Saying so is what stops a receiving clerk treating an empty field
 * as a discrepancy.
 */
export function UnitIdentity({
  serialNumbers,
  batchNumber,
  quantity,
}: {
  serialNumbers?: string[] | null;
  batchNumber?: string | null;
  quantity?: number | null;
}) {
  const serials = serialNumbers ?? [];

  if (serials.length === 0 && !batchNumber) {
    return (
      <span style={{ color: "#888" }}>
        Not yet identified — established at goods-in
      </span>
    );
  }

  if (serials.length > 0) {
    // A line for two units with one serial named is a real and legitimate
    // state (the rest backordered), but it is worth showing rather than
    // hiding: receiving can only match the certificate for the unit named.
    const partial =
      typeof quantity === "number" && quantity > serials.length
        ? ` — ${serials.length} of ${quantity} identified`
        : "";
    return (
      <span>
        <code style={{ fontSize: 12 }}>{serials.join(", ")}</code>
        {partial && <span style={{ color: "#888" }}>{partial}</span>}
      </span>
    );
  }

  return (
    <span>
      Lot <code style={{ fontSize: 12 }}>{batchNumber}</code>
    </span>
  );
}

/**
 * How much of the order actually turned up.
 *
 * The order status cannot answer this: it is binary, so one-of-two and
 * two-of-two both read `Received`. When only part of a line arrives the demand
 * behind it stays open — a receipt says how many units came, not which
 * requirement they were allocated to, so a short shipment cannot be attributed
 * and closing anything would over-close. That is correct, and it is also the
 * thing that looks like a bug from the screen: a received order, a passed
 * inspection and a requirement still sitting open.
 *
 * So the shortfall is stated, and stated as the reason. Silent on a full
 * receipt beyond the count, and silent entirely before one — a shipment in
 * transit has nothing to be short of yet.
 */
export function ReceivedCount({
  received,
  ordered,
}: {
  received?: number | null;
  ordered?: number | null;
}) {
  if (received == null) return null;

  if (ordered == null) {
    return <span>{received} received</span>;
  }

  const short = received < ordered;
  return (
    <span
      style={short ? { color: "#8a5a1c", fontWeight: 600 } : undefined}
      title={
        short
          ? "Short shipment. The demand behind this order stays open until the " +
            "balance arrives or a planner closes it by hand."
          : undefined
      }
    >
      {short && "⚠ "}
      {received} of {ordered} received
    </span>
  );
}

/**
 * The traceability terms the order was placed on.
 *
 * Deliberately shows the *requirement* and not the verdict on the supplier's
 * quote. By the time an order exists that decision has been taken — repeating
 * "conflicting, overridden by a manager" here would re-open a settled question
 * on a screen that cannot act on it. What this screen is for is telling whoever
 * receives the part what to check the certificate against.
 */
export function TraceTermsPanel({
  commitment,
}: {
  commitment?: TraceCommitment | null;
}) {
  const requirement = commitment?.requirement;
  if (!requirement || requirement.acceptable_cert_types.length === 0) {
    return null;
  }

  const certs = requirement.acceptable_cert_types.map(certLabel).join(" or ");
  const overridden = !!commitment?.acknowledged;

  return (
    <div
      style={{
        background: overridden ? "#fffaf0" : "#f4f8ff",
        border: `1px solid ${overridden ? "#ffd8a8" : "#c9dcff"}`,
        borderRadius: 4,
        padding: "6px 8px",
        margin: "10px 0 0",
        fontSize: 12,
        color: overridden ? "#8a5a1c" : "#1c4b8a",
      }}
    >
      <strong>Required at goods-in: {certs}</strong>
      {requirement.back_to_birth_required && (
        <div style={{ marginTop: 4 }}>
          Complete back-to-birth record required — this part is life-limited.
        </div>
      )}
      {requirement.airworthiness_release_required && (
        <div style={{ marginTop: 4 }}>
          Must be an airworthiness release; a conformity statement is not
          accepted for condition {requirement.condition_code}.
        </div>
      )}
      {!requirement.part_class_known && (
        <div style={{ marginTop: 4, opacity: 0.8 }}>
          Derived from the condition code alone — this part has no class on the
          part master, so the requirement is the conservative one.
        </div>
      )}
      {overridden && (
        // Stated on the order, not just in the audit log: a receiving clerk
        // holding a part whose paperwork falls short needs to know that was a
        // decision someone took, not an error to escalate.
        <div style={{ marginTop: 4, fontWeight: 600 }}>
          Ordered on an acknowledged traceability shortfall.
        </div>
      )}
    </div>
  );
}
